import { useCallback, useEffect, useMemo, useState } from "react";
import { ActivityIndicator, StyleSheet, View } from "react-native";
import * as SplashScreen from "expo-splash-screen";
import { KeyboardProvider } from "react-native-keyboard-controller";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import ScreenCard from "./ScreenCard";
import ScreenTitle from "../components/ScreenTitle";
import MarkdownView from "../components/MarkdownView";
import Text from "../components/Text";
import HStack from "../ui_atoms/HStack";
import Gap from "../ui_atoms/Gap";
import { useI18n } from "../hooks/i18n-hooks";
import { useTheme } from "../hooks/use-theme";
import { useLiftFont } from "../contexts/AppLoadingManager";
import MaintenanceSvg from "../assets/images/live_main.svg";

const ILLUSTRATION_WIDTH = 250;
// live_main.svg viewBox is 375x382
const ILLUSTRATION_HEIGHT = Math.round((ILLUSTRATION_WIDTH * 382) / 375);
const CONTENT_MAX_WIDTH = 500;

// Centers the text of each paragraph (textAlign) and the paragraph itself when it is shorter than
// a line (justifyContent: MarkdownView lays every paragraph out in a row).
const DESCRIPTION_MARKDOWN_STYLE = {
    paragraph: { textAlign: "center" as const, justifyContent: "center" as const },
};

type MaintenanceScreenProps = {
    /** Checks right away whether the server is back. */
    onRetry: () => Promise<void>;
};

/**
 * Shown in place of the whole app while the server is in maintenance (see MaintenanceManager).
 * It can be the very first screen of a cold start, before the app loaded its fonts and hid the
 * splash screen, so it does both itself.
 */
export const MaintenanceScreen = ({ onRetry }: MaintenanceScreenProps) => {
    const i18n = useI18n();
    const styles = useStyles();
    const { colors } = useTheme();
    const insets = useSafeAreaInsets();
    const [fontsLoaded, fontsError] = useLiftFont();
    const isReady = fontsLoaded || !!fontsError;
    const [isRetrying, setIsRetrying] = useState(false);

    useEffect(() => {
        if (isReady) {
            SplashScreen.hide();
        }
    }, [isReady]);

    const onRetryPress = useCallback(async () => {
        setIsRetrying(true);
        try {
            await onRetry();
        } finally {
            setIsRetrying(false);
        }
    }, [onRetry]);

    if (!isReady) {
        return null;
    }

    return (
        // This screen replaces the subtree holding the app's KeyboardProvider, and ScreenCard's
        // KeyboardAwareScrollView crashes without a keyboard context.
        <KeyboardProvider>
            <ScreenCard
                buttons={[
                    {
                        title: i18n.t("retry"),
                        onPress: () => void onRetryPress(),
                        validating: isRetrying,
                        mode: "outlined",
                        testID: "maintenance-retry",
                    },
                ]}
                contentStyle={{ paddingTop: insets.top }}
                isContentContainerFlex
            >
                <View style={styles.content} testID="maintenance-screen">
                    <MaintenanceSvg width={ILLUSTRATION_WIDTH} height={ILLUSTRATION_HEIGHT} />
                    <Gap gap="2/3" />
                    <ScreenTitle style={styles.title}>
                        {i18n.t("screens.maintenance_screen.title")}
                    </ScreenTitle>
                    <Gap gap="1/3" />
                    <MarkdownView
                        style={styles.description}
                        markdown={i18n.t("screens.maintenance_screen.description")}
                        markdownStyle={DESCRIPTION_MARKDOWN_STYLE}
                    />
                    <Gap gap="2/3" />
                    <HStack gap="1/3">
                        <ActivityIndicator size="small" color={colors.greyAccessible} />
                        <Text style={styles.autoRetry}>
                            {i18n.t("screens.maintenance_screen.auto_retry")}
                        </Text>
                    </HStack>
                </View>
            </ScreenCard>
        </KeyboardProvider>
    );
};

const useStyles = () => {
    const { marginSize, fonts, colors } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                content: {
                    alignItems: "center",
                    alignSelf: "center",
                    width: "100%",
                    maxWidth: CONTENT_MAX_WIDTH,
                    paddingHorizontal: marginSize,
                },
                title: {
                    textAlign: "center",
                },
                description: {
                    alignSelf: "stretch",
                },
                autoRetry: {
                    ...fonts.bodySmall,
                    color: colors.greyAccessible,
                },
            }),
        [colors.greyAccessible, fonts.bodySmall, marginSize],
    );
};
